import { DEFAULT_MYAUCTION_LIST_LENGTH } from 'constants/auctions';
import { updateArrObjKey } from 'utils/filters';
import {
  ACTIVE_AUCTIONS_FETCH_LISTINGS,
  ACTIVE_AUCTIONS_FETCH_AUCTIONS,
  FETCH_PUBLIC_PREVIEW_AUCTIONS,
  FETCH_PUBLIC_ACTIVE_AUCTIONS,
  ACTIVE_AUCTIONS_SEARCH_DICTIONARIES,
  ACTIVE_AUCTIONS_PUBLIC_FETCH_LISTINGS,
  AUCTIONS_FETCH_JEWEL_LISTING,
  BID_MODAL_CREATE_BID,
  BID_MODAL_DELETE_BID,
  AUCTIONS_JEWEL_LISTING_CREATE_FAVORITE,
  AUCTIONS_JEWEL_LISTING_DELETE_FAVORITE,
  AUCTIONS_JEWEL_LISTING_UPDATE_FILTER,
  AUCTIONS_JEWEL_LISTINGS_RESET,
  ACTIVE_AUCTIONS_CHANGE_TABS,
  BID_MODAL_RESOLVE_OFFER,
  AUCTIONS_HIGHEST_BID_CHANGED,
  FETCH_PUBLIC_PREVIEW_LISTINGS
} from 'constants/action-types';
import { listingStatuses } from 'constants/listings';

const getStatus = (lot, data) => {
  switch (true) {
    case data.ListingStatus === 'Sold':
      if (!lot.requesterInfo.isBuyer && !lot.requesterInfo.isSeller) {
        return {
          listingState: listingStatuses.EndedSold.name,
          listingStatus: data.ListingStatus
        };
      } else {
        return {
          listingState: lot.requesterInfo.isBuyer
            ? listingStatuses.EndedBuyerWon.name
            : listingStatuses.EndedSellerSold.name,
          listingStatus: data.ListingStatus
        };
      }
    case data.ListingStatus === 'Active' &&
      (data.BidCount === 0 || data.BidCount > 0) &&
      !lot.requesterBidPrice:
      return {
        listingState:
          lot.requesterInfo.isBuyer || (!lot.requesterInfo.isBuyer && !lot.requesterInfo.isSeller)
            ? listingStatuses.ActiveBuyerNewBid.name
            : listingStatuses.ActiveSellerViewBid.name,
        listingStatus: data.ListingStatus
      };
    case data.ListingStatus === 'Active' &&
      data.BidCount > 0 &&
      lot.requesterBidPrice &&
      +lot.requesterBidPrice > 0:
      return {
        listingState:
          lot.requesterInfo.isBuyer || (!lot.requesterInfo.isBuyer && !lot.requesterInfo.isSeller)
            ? listingStatuses.ActiveBuyerUpdateBid.name
            : listingStatuses.ActiveSellerViewBid.name,
        listingStatus: data.ListingStatus
      };
    case data.ListingStatus === 0 && lot.requesterInfo.isSeller:
      return {
        listingState: listingStatuses.ActiveSellerViewBid.name
      };
    default:
      return {};
  }
};

export const defaultFilters = {
  onlyFavorites: false,
  onlyMyBids: false,
  onlyMyListings: false,
  estimateLow: null,
  estimateHigh: null,
  freeSearchString: '',
  catalogNumber: null,
  jewelryStyles: [],
  jewelryTypes: [],
  categories: [],
  brandsAndDesigners: [],
  estimateRanges: [],
  gemstones: [],
  metals: [],
  sortBy: []
};

const initialState = {
  auctions: [],
  pagination: {
    numberOfPages: 1,
    currentPage: 1,
    recordsPerPage: DEFAULT_MYAUCTION_LIST_LENGTH
  },
  listings: [],
  activeAuctionId: null,
  isListingsLoading: false,
  isAuctionsLoading: false,
  isAuctionsLoaded: false,
  isAnyListingsLoaded: false,
  chosenFilters: { ...defaultFilters },
  searchDictionaries: {
    jewelryTypes: {},
    styles: {},
    brandsAndDesigners: {},
    estimationRanges: {},
    categories: {},
    gemstones: {},
    metals: {}
  }
};

export const activeAuction = (state = initialState, action) => {
  switch (action.type) {
    case ACTIVE_AUCTIONS_FETCH_LISTINGS.pending:
    case ACTIVE_AUCTIONS_PUBLIC_FETCH_LISTINGS.pending:
    case FETCH_PUBLIC_PREVIEW_LISTINGS.pending:
      return {
        ...state,
        listings: initialState.listings,
        isListingsLoading: true
      };
    case ACTIVE_AUCTIONS_FETCH_LISTINGS.success:
    case ACTIVE_AUCTIONS_PUBLIC_FETCH_LISTINGS.success:
    case FETCH_PUBLIC_PREVIEW_LISTINGS.success:
      return {
        ...state,
        listings: action.payload.listings,
        pagination: {
          numberOfPages: Math.ceil(
            action.payload.pagination.totalRecordsFound / action.payload.pagination.recordsPerPage
          ),
          ...action.payload.pagination
        },
        activeAuctionId: action.meta.requestedAuctionId || state.activeAuctionId,
        isListingsLoading: false,
        isAnyListingsLoaded: true,
        chosenFilters: {
          ...state.chosenFilters,
          ...action.payload.chosenFilters
        }
      };
    case ACTIVE_AUCTIONS_FETCH_AUCTIONS.pending:
    case FETCH_PUBLIC_PREVIEW_AUCTIONS.pending:
    case FETCH_PUBLIC_ACTIVE_AUCTIONS.pending:
      return {
        ...state,
        isAuctionsLoaded: false,
        isAuctionsLoading: true,
        activeAuctionId: null,
        listings: []
      };
    case ACTIVE_AUCTIONS_FETCH_AUCTIONS.success:
    case FETCH_PUBLIC_PREVIEW_AUCTIONS.success:
    case FETCH_PUBLIC_ACTIVE_AUCTIONS.success:
      return {
        ...state,
        isAuctionsLoaded: true,
        isAuctionsLoading: false,
        activeAuctionId:
          action.meta.activeAuction ||
          state.activeAuctionId ||
          (action.payload.auctions.length && action.payload.auctions[0].auctionId),
        auctions: action.payload.auctions
      };
    case ACTIVE_AUCTIONS_SEARCH_DICTIONARIES.success:
      return {
        ...state,
        searchDictionaries: action.payload
      };
    case BID_MODAL_CREATE_BID.pending:
      return {
        ...state,
        listings: state.listings.map(l =>
          l.listingId === action.payload.data.listingId ? { ...l, requesterBidPrice: null } : l
        )
      };
    case BID_MODAL_CREATE_BID.success:
      return {
        ...state,
        listings: state.listings.map(l =>
          l.listingId === action.payload.listingId
            ? { ...l, requesterBidPrice: action.payload.bidPrice }
            : l
        )
      };
    case BID_MODAL_DELETE_BID.success:
      return {
        ...state,
        listings: updateArrObjKey(
          state.listings,
          { listingId: action.meta.listingId },
          'listingId',
          'requesterBidPrice'
        )
      };
    case AUCTIONS_FETCH_JEWEL_LISTING.success: {
      return {
        ...state,
        listings: state.listings.map(l =>
          l.listingId === action.payload.listings[0].listingId ? action.payload.listings[0] : l
        )
      };
    }

    case AUCTIONS_JEWEL_LISTING_CREATE_FAVORITE.success:
    case AUCTIONS_JEWEL_LISTING_DELETE_FAVORITE.success:
      return {
        ...state,
        listings: updateArrObjKey(state.listings, action.payload, 'listingId', 'isFavorite')
      };
    case BID_MODAL_RESOLVE_OFFER.success:
      return {
        ...state,
        listings: updateArrObjKey(state.listings, action.payload, 'listingId', 'listingState')
      };
    case AUCTIONS_JEWEL_LISTING_UPDATE_FILTER:
      return {
        ...state,
        chosenFilters: { ...state.chosenFilters, ...action.payload }
      };
    case AUCTIONS_JEWEL_LISTINGS_RESET:
      return {
        ...state,
        chosenFilters: { ...initialState.chosenFilters },
        listings: []
      };
    case ACTIVE_AUCTIONS_CHANGE_TABS:
      return {
        ...state,
        activeAuctionId: null,
        chosenFilters: {
          ...defaultFilters
        },
        isAuctionsLoaded: false
      };
    case AUCTIONS_HIGHEST_BID_CHANGED: {
      const newListings = state.listings.map(lot => {
        if (lot.listingId === action.payload.data.ListingId) {
          return {
            ...lot,
            highestBidPrice: action.payload.data.BidPrice,
            startPrice: action.payload.data.BidPrice ? null : lot.startPrice,
            bidIncrementAmount: action.payload.data.BidIncrementAmount,
            ...getStatus(lot, action.payload.data),
            sellerSaleTotalPriceWithCommission: lot.sellerCommission
              ? action.payload.data.BidPrice -
                (action.payload.data.BidPrice * lot.sellerCommission) / 100
              : null,
            endDate: action.payload.data.EndDateTime && action.payload.data.EndDateTime
          };
        }
        return lot;
      });
      const newAuctions = state.auctions.map(auction => {
        if (auction.auctionId === action.payload.data.AuctionId) {
          return {
            ...auction,
            endDate: action.payload.data.EndDateTime && action.payload.data.EndDateTime
          };
        }
        return auction;
      });
      return {
        ...state,
        listings: newListings,
        auctions: newAuctions
      };
    }
    default:
      return state;
  }
};
